import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { MultiSelect } from 'primereact/multiselect'
import { ProgressSpinner } from 'primereact/progressspinner'
import { RadioButton } from 'primereact/radiobutton'
import { TabPanel, TabView } from 'primereact/tabview'
import { toast } from 'react-hot-toast'

import { useBulkResubmitTasks, useGetFailedTasks } from '../api/failed-tasks-API'
import StalledStarccmTasks from '../components/admin/StalledStarccmTasks'
import routes from '../routing/routes'
import { RunStatus } from '../types/types'
import { WorkflowStatusDef } from '../utils/constants'
import {
  DropdownStyle,
  MultiSelectCheckboxStyle,
  TablePaginatorStyle
} from '../utils/styling-constants'
import { currentUserState } from '../utils/state-atoms'

type FailedTaskRow = {
  submissionId: string
  workflowSubmissionId?: string
  submissionName?: string
  projectName?: string
  caseId?: string
  caseName?: string
  workflowRunGroupSysId?: string
  resubmitAttempts?: string
  workflowSystemRunId?: string
  workflowName?: string
  taskId?: string
  firstTaskId?: string
  taskMongoId?: string
  taskName?: string
  taskType?: string
  status?: RunStatus | string
  failureReason?: string
  httpResultCode?: number
  runCount?: number
  runLog?: string[]
  runVersion?: string
  submitTime?: string
  rescaleJob?: { jobId?: string; jobName?: string; [key: string]: unknown }
  failureTime?: number | null
  lastSubmittedBy?: string
  lastSubmittedByName?: string
  lastSubmittedByEmail?: string
  submitUserId?: string
  submitByUser?: string
  lastSubmissionTime?: number
}

type FilterForm = {
  workflowType: string
  submissionId: string
  caseId: string
  taskName: string
  statuses: string[]
  failedDateRange: (Date | null)[] | null
  submissionDateRange: (Date | null)[] | null
  runVersion: string
  includeReferenceRuns: boolean
}

// Workflow type options — matched server-side (case-insensitive) against the workflow name.
// 'ALL' is a sentinel meaning "no filter"; PrimeReact Dropdown doesn't reliably select an
// option whose value is an empty string.
const ALL_WORKFLOW_TYPES = 'ALL'
const WORKFLOW_TYPE_OPTIONS = [
  { label: 'All Workflow Types', value: ALL_WORKFLOW_TYPES },
  { label: 'Ensight Workflow', value: 'Ensight' },
  { label: 'StarCCM Workflow', value: 'StarCCM' },
  { label: 'Post Processing Workflow', value: 'Post' },
  { label: 'MAV Workflow', value: 'MAV' }
]

const PRESET_FILTER_OPTIONS = [
  { label: 'Submitted Last 30 Days', value: 'submittedLast30Days' },
  {
    label: 'Completed with References Last 30 Days',
    value: 'completedWithReferencesLast30Days'
  }
]

// Task statuses eligible for resubmission. COMPLETED cases are included so post-processing can
// be regenerated against a newer Post Processing version, not just failed tasks recovered.
const STATUS_OPTIONS = [
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Error', value: 'ERROR' },
  { label: 'Warn on Error', value: 'WARN_ON_ERROR' }
]

const DEFAULT_FILTER_VALUES: FilterForm = {
  workflowType: ALL_WORKFLOW_TYPES,
  submissionId: '',
  caseId: '',
  taskName: '',
  statuses: [],
  failedDateRange: null,
  submissionDateRange: null,
  runVersion: '',
  includeReferenceRuns: false
}

const DEFAULT_ROWS = 25

const getDateRangeBounds = (range: (Date | null)[] | null) => {
  const from = range?.[0] ? new Date(range[0]) : null
  const to = range?.[1] ? new Date(range[1]) : null
  if (from) from.setHours(0, 0, 0, 0)
  if (to) to.setHours(23, 59, 59, 999)
  return {
    from: from ? from.getTime() : undefined,
    to: to ? to.getTime() : undefined
  }
}

const formatEpoch = (value?: number | string | null): string => {
  if (value === undefined || value === null || value === '') return '—'
  const numeric = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(numeric)) return '—'
  const epochMs = numeric < 1e12 ? numeric * 1000 : numeric
  const date = new Date(epochMs)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

const statusLabel = (status?: RunStatus | string): string => {
  if (status === undefined || status === null || status === '') return 'Unknown'
  // runStatus arrives from the API as the enum name (e.g. 'ERROR'); numeric values map via WorkflowStatusDef.
  if (typeof status === 'string') {
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')
  }
  return WorkflowStatusDef[status as keyof typeof WorkflowStatusDef]?.name ?? String(status)
}

const isErrorStatus = (status?: RunStatus | string): boolean =>
  status === RunStatus.ERROR || status === 'ERROR'

const isCompletedStatus = (status?: RunStatus | string): boolean =>
  status === RunStatus.COMPLETED || status === 'COMPLETED'

// Best-available display name for the task's original submitting user.
const submitterLabel = (row: {
  lastSubmittedByName?: string
  lastSubmittedBy?: string
  lastSubmittedByEmail?: string
  submitByUser?: string
}): string =>
  row.lastSubmittedByName?.trim() ||
  row.lastSubmittedBy ||
  row.lastSubmittedByEmail ||
  row.submitByUser ||
  'Unknown'

const AdminTaskManagement: FC = () => {
  const currentUser = useRecoilValue(currentUserState)
  const navigate = useNavigate()

  const [{ data: failedData, loading: loadingTasks }, fetchFailedTasks] = useGetFailedTasks()
  const [{ loading: resubmitting }, bulkResubmit] = useBulkResubmitTasks()

  const [tasks, setTasks] = useState<FailedTaskRow[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedTasks, setSelectedTasks] = useState<FailedTaskRow[]>([])
  const [selectedTask, setSelectedTask] = useState<FailedTaskRow | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  // When true, resubmit restarts each workflow from its first task instead of the failed task.
  const [resubmitFromFirstTask, setResubmitFromFirstTask] = useState(false)

  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(DEFAULT_ROWS)
  const [sortField, setSortField] = useState<string>('failureTime')
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(-1)
  const [selectedPresetFilter, setSelectedPresetFilter] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0)

  const { control, handleSubmit, reset, getValues } = useForm<FilterForm>({
    defaultValues: DEFAULT_FILTER_VALUES
  })

  // Restrict access to admin and developer roles only
  useEffect(() => {
    if (
      currentUser &&
      currentUser.role?.roleId !== 'admin' &&
      currentUser.role?.roleId !== 'developer'
    ) {
      navigate(routes.Dashboard.absolutePath, { replace: true })
    }
  }, [currentUser, navigate])

  const runSearch = useCallback(
    (paging: { first: number; rows: number; sortField: string; sortOrder: 1 | -1 | 0 }) => {
      const values = getValues()
      const failedRange = getDateRangeBounds(values.failedDateRange)
      const submissionRange = getDateRangeBounds(values.submissionDateRange)

      fetchFailedTasks({
        workflowType:
          values.workflowType && values.workflowType !== ALL_WORKFLOW_TYPES
            ? values.workflowType
            : undefined,
        submissionId: values.submissionId || undefined,
        caseId: values.caseId || undefined,
        taskName: values.taskName || undefined,
        status: values.statuses?.length ? values.statuses.join(',') : undefined,
        failedFrom: failedRange.from,
        failedTo: failedRange.to,
        submissionFrom: submissionRange.from,
        submissionTo: submissionRange.to,
        runVersion: values.runVersion || undefined,
        includeReferenceRuns: values.includeReferenceRuns || undefined,
        skip: paging.first,
        limit: paging.rows,
        sortField: paging.sortField,
        sortOrder: paging.sortOrder === 1 ? 'asc' : 'desc'
      }).catch(() => {
        toast.error('Failed to load failed tasks.')
      })
    },
    [fetchFailedTasks, getValues]
  )

  useEffect(() => {
    const payload = failedData as { data?: FailedTaskRow[]; totalCount?: number } | undefined
    if (payload?.data) {
      setTasks(payload.data)
      setTotalRecords(payload.totalCount ?? payload.data.length)
    }
  }, [failedData])

  const onSearch = () => {
    setFirst(0)
    setSelectedTasks([])
    runSearch({ first: 0, rows, sortField, sortOrder })
  }

  const onClear = () => {
    reset(DEFAULT_FILTER_VALUES)
    setSelectedPresetFilter(null)
    setFirst(0)
    setSelectedTasks([])
    setTasks([])
    setTotalRecords(0)
  }

  const applyPresetFilter = (presetFilter: string | null) => {
    setSelectedPresetFilter(presetFilter)
    if (presetFilter === 'submittedLast30Days') {
      const today = new Date()
      const thirtyDaysAgo = new Date(today)
      thirtyDaysAgo.setDate(today.getDate() - 30)
      reset({
        ...DEFAULT_FILTER_VALUES,
        submissionDateRange: [thirtyDaysAgo, today]
      })
      return
    }
    if (presetFilter === 'completedWithReferencesLast30Days') {
      const today = new Date()
      const thirtyDaysAgo = new Date(today)
      thirtyDaysAgo.setDate(today.getDate() - 30)
      reset({
        ...DEFAULT_FILTER_VALUES,
        statuses: ['COMPLETED'],
        submissionDateRange: [thirtyDaysAgo, today],
        includeReferenceRuns: true
      })
      return
    }
    reset(DEFAULT_FILTER_VALUES)
  }

  const onPage = (event: DataTablePageEvent) => {
    setFirst(event.first)
    setRows(event.rows)
    runSearch({ first: event.first, rows: event.rows, sortField, sortOrder })
  }

  const onSort = (event: DataTableSortEvent) => {
    const nextField = event.sortField || 'failureTime'
    const nextOrder = (event.sortOrder ?? -1) as 1 | -1 | 0
    setSortField(nextField)
    setSortOrder(nextOrder)
    runSearch({ first, rows, sortField: nextField, sortOrder: nextOrder })
  }

  const openDetails = (task: FailedTaskRow) => {
    setSelectedTask(task)
    setShowDetails(true)
  }

  const confirmResubmit = () => {
    if (selectedTasks.length === 0) {
      toast.error('Select at least one task to resubmit.')
      return
    }
    setShowConfirm(true)
  }

  const handleResubmit = async () => {
    // Resubmitting from the first task is a whole-workflow operation, so collapse multiple
    // selected tasks that belong to the same workflow into a single resubmit to avoid
    // starting the same workflow more than once.
    const tasksToSubmit = resubmitFromFirstTask
      ? Array.from(
          new Map(
            selectedTasks.map(task => [
              `${task.submissionId}|${task.workflowRunGroupSysId}|${task.workflowSystemRunId}`,
              task
            ])
          ).values()
        )
      : selectedTasks

    const payload = tasksToSubmit.map(task => ({
      submissionId: task.submissionId,
      workflowRunGroupSysId: task.workflowRunGroupSysId,
      workflowSystemRunId: task.workflowSystemRunId,
      taskId: task.taskId,
      resubmitFromTaskId:
        resubmitFromFirstTask && task.firstTaskId ? task.firstTaskId : task.taskId,
      submitUserId: task.submitUserId,
      submitByUser: task.submitByUser,
      submitUserEmail: task.lastSubmittedByEmail
    }))

    try {
      const response = await bulkResubmit(payload as unknown[])
      const result = response?.data as
        | {
            succeeded?: number
            failed?: number
            total?: number
            results?: Array<{ taskId?: string; success: boolean; message: string }>
          }
        | undefined
      const succeeded = result?.succeeded ?? 0
      const failed = result?.failed ?? 0
      if (failed === 0) {
        toast.success(`Resubmitted ${succeeded} task${succeeded === 1 ? '' : 's'}.`)
      } else {
        const failures = (result?.results ?? []).filter(r => !r.success)
        console.error('Resubmit failures:', failures)
        const reasons = Array.from(new Set(failures.map(r => r.message)))
          .slice(0, 2)
          .join('; ')
        toast.error(`Resubmitted ${succeeded}, ${failed} failed${reasons ? `: ${reasons}` : '.'}`, {
          duration: 8000
        })
      }
      setShowConfirm(false)
      setSelectedTasks([])
      runSearch({ first, rows, sortField, sortOrder })
    } catch (error) {
      console.error(error)
      toast.error('Failed to resubmit selected tasks.')
    }
  }

  const failureTimeBody = (row: FailedTaskRow) => formatEpoch(row.failureTime)
  const submissionTimeBody = (row: FailedTaskRow) => formatEpoch(row.lastSubmissionTime)
  const statusBody = (row: FailedTaskRow) => (
    <span
      className={
        isErrorStatus(row.status)
          ? 'text-red-600 dark:text-red-400 font-medium'
          : isCompletedStatus(row.status)
            ? 'text-green-600 dark:text-green-400 font-medium'
            : 'text-amber-600 dark:text-amber-400 font-medium'
      }>
      {statusLabel(row.status)}
    </span>
  )
  const taskNameBody = (row: FailedTaskRow) => (
    <button
      className="text-gm-blue dark:text-light-blue hover:underline outline-none text-left"
      onClick={() => openDetails(row)}>
      {row.taskName || '—'}
    </button>
  )
  const caseIdBody = (row: FailedTaskRow) => {
    const value = row.caseName || row.caseId || '—'
    return (
      <div className="max-w-40 overflow-x-auto whitespace-nowrap" title={value}>
        {value}
      </div>
    )
  }

  const selectedCount = selectedTasks.length

  // Distinct workflows among the selected tasks (used for first-task resubmit collapsing).
  const distinctWorkflowCount = new Set(
    selectedTasks.map(t => `${t.submissionId}|${t.workflowRunGroupSysId}|${t.workflowSystemRunId}`)
  ).size

  const detailRows = useMemo(() => {
    if (!selectedTask) return []
    return [
      { label: 'Workflow Type', value: selectedTask.workflowName },
      { label: 'Task Name', value: selectedTask.taskName },
      { label: 'Task ID', value: selectedTask.taskId },
      { label: 'Status', value: statusLabel(selectedTask.status) },
      { label: 'Submission', value: selectedTask.submissionName || selectedTask.submissionId },
      { label: 'Project', value: selectedTask.projectName },
      { label: 'Case', value: selectedTask.caseName || selectedTask.caseId },
      { label: 'Failure Time', value: formatEpoch(selectedTask.failureTime) },
      { label: 'Post-Pro Version', value: selectedTask.runVersion },
      {
        label: 'Original Submitter',
        value:
          selectedTask.lastSubmittedByName?.trim() ||
          selectedTask.lastSubmittedBy ||
          selectedTask.submitByUser
      },
      { label: 'Last Submission Time', value: formatEpoch(selectedTask.lastSubmissionTime) },
      {
        label: 'Retry Count',
        value: selectedTask.runCount != null ? String(selectedTask.runCount) : '—'
      },
      { label: 'Resubmit Attempts', value: selectedTask.resubmitAttempts || '—' },
      {
        label: 'Rescale Job',
        value: selectedTask.rescaleJob?.jobId || selectedTask.rescaleJob?.jobName || 'N/A'
      }
    ]
  }, [selectedTask])

  return (
    <div className="p-4 space-y-4 dark:text-neutral-10">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Admin Task Management</h1>
        <div className="flex items-end gap-4">
          {activeTab === 0 && (
            <div className="flex flex-col min-w-64">
              <label className="text-sm mb-1">Preset Filters</label>
              <Dropdown
                value={selectedPresetFilter}
                options={PRESET_FILTER_OPTIONS}
                onChange={e => applyPresetFilter(e.value)}
                placeholder="Select Preset Filter"
                showClear
                pt={DropdownStyle}
              />
            </div>
          )}
          {(loadingTasks || resubmitting) && <ProgressSpinner style={{ width: 28, height: 28 }} />}
        </div>
      </div>
      <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)}>
        <TabPanel header="Failed Tasks">
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-neutral-20">
              Identify failed workflow tasks and resubmit them in bulk during large-scale recovery
              scenarios.
            </p>

            {/* Filters */}
            <form
              onSubmit={handleSubmit(onSearch)}
              className="dark:bg-neutral-80 bg-gray-50 p-4 rounded-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm mb-1">Workflow Type</label>
                <Controller
                  name="workflowType"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={WORKFLOW_TYPE_OPTIONS}
                      placeholder="Select Workflow Type"
                      pt={DropdownStyle}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Submission ID</label>
                <Controller
                  name="submissionId"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} placeholder="Submission ID or name" className="w-full" />
                  )}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Case ID</label>
                <Controller
                  name="caseId"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} placeholder="Case ID or name" className="w-full" />
                  )}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Task Name</label>
                <Controller
                  name="taskName"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} placeholder="Task name" className="w-full" />
                  )}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Status</label>
                <Controller
                  name="statuses"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      value={field.value}
                      onChange={e => field.onChange(e.value)}
                      options={STATUS_OPTIONS}
                      placeholder="All eligible statuses"
                      showClear
                      className="w-full"
                      pt={MultiSelectCheckboxStyle}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Post-Pro Version</label>
                <Controller
                  name="runVersion"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} placeholder="Post-Pro version" className="w-full" />
                  )}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Controller
                  name="includeReferenceRuns"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      inputId="includeReferenceRuns"
                      checked={field.value}
                      onChange={e => field.onChange(e.checked ?? false)}
                    />
                  )}
                />
                <label htmlFor="includeReferenceRuns" className="text-sm cursor-pointer">
                  Include Reference Runs
                </label>
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Failed Date Range</label>
                <Controller
                  name="failedDateRange"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      value={field.value as Date[] | null}
                      onChange={e => field.onChange(e.value)}
                      selectionMode="range"
                      readOnlyInput
                      showButtonBar
                      placeholder="Start – End"
                      className="w-full"
                    />
                  )}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm mb-1">Submission Date</label>
                <Controller
                  name="submissionDateRange"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      value={field.value as Date[] | null}
                      onChange={e => field.onChange(e.value)}
                      selectionMode="range"
                      readOnlyInput
                      showButtonBar
                      placeholder="Start - End"
                      className="w-full"
                    />
                  )}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" label="Search" icon="pi pi-search" loading={loadingTasks} />
                <Button
                  type="button"
                  label="Clear"
                  severity="secondary"
                  outlined
                  className="!border-gray-500 dark:!border-neutral-20"
                  pt={{ label: { className: '!text-white' } }}
                  onClick={onClear}
                />
              </div>
            </form>

            {/* Bulk action bar */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-neutral-20">
                {selectedCount > 0 ? `${selectedCount} selected` : `${totalRecords} failed task(s)`}
              </span>
              <Button
                label="Resubmit Selected Tasks"
                icon="pi pi-replay"
                disabled={selectedCount === 0 || resubmitting}
                onClick={confirmResubmit}
              />
            </div>

            {/* Failed tasks table */}
            <DataTable
              value={tasks}
              dataKey="taskId"
              loading={loadingTasks}
              selectionMode="multiple"
              selection={selectedTasks}
              onSelectionChange={e => setSelectedTasks(e.value as FailedTaskRow[])}
              paginator
              lazy
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              rowsPerPageOptions={[25, 50, 100]}
              onPage={onPage}
              onSort={onSort}
              sortField={sortField}
              sortOrder={sortOrder}
              removableSort
              emptyMessage="No failed tasks found. Adjust filters and search."
              pt={{ paginator: TablePaginatorStyle } as any}>
              <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
              <Column field="workflowName" header="Workflow Type" sortable />
              <Column field="submissionName" header="Submission ID" sortable />
              <Column
                field="caseName"
                header="Case ID"
                body={caseIdBody}
                style={{ width: '10rem', maxWidth: '10rem' }}
                headerStyle={{ width: '10rem', maxWidth: '10rem' }}
              />
              <Column field="taskId" header="Task ID" />
              <Column field="taskName" header="Task Name" body={taskNameBody} sortable />
              <Column field="status" header="Status" body={statusBody} sortable />
              <Column field="failureTime" header="Failure Time" body={failureTimeBody} sortable />
              <Column field="runVersion" header="Post-Pro Version" sortable />
              <Column
                field="failureReason"
                header="Failure Reason"
                body={(row: FailedTaskRow) => (
                  <span className="line-clamp-2" title={row.failureReason}>
                    {row.failureReason || '—'}
                  </span>
                )}
              />
              <Column
                field="lastSubmittedBy"
                header="Last Submitted By"
                body={(row: FailedTaskRow) =>
                  row.lastSubmittedByName?.trim() || row.lastSubmittedBy || '—'
                }
                sortable
              />
              <Column
                field="lastSubmissionTime"
                header="Last Submission Time"
                body={submissionTimeBody}
                sortable
              />
            </DataTable>
          </div>
        </TabPanel>
        <TabPanel header="Stalled StarCCM">
          <StalledStarccmTasks />
        </TabPanel>
      </TabView>

      {/* Task details panel */}
      <Dialog
        header="Task Details"
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        position="right"
        style={{ width: '38vw', minWidth: 360 }}>
        {selectedTask && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-2">
              {detailRows.map(row => (
                <div key={row.label} className="grid grid-cols-3 gap-2 text-sm">
                  <span className="font-medium text-gray-500 dark:text-neutral-20">
                    {row.label}
                  </span>
                  <span className="col-span-2 break-words">{row.value || '—'}</span>
                </div>
              ))}
            </div>

            <div>
              <div className="font-medium text-sm mb-1">Full Error Message</div>
              <pre className="text-xs whitespace-pre-wrap bg-gray-100 dark:bg-neutral-90 p-2 rounded max-h-48 overflow-auto">
                {selectedTask.failureReason || 'No error message available.'}
              </pre>
            </div>

            {selectedTask.runLog && selectedTask.runLog.length > 0 && (
              <div>
                <div className="font-medium text-sm mb-1">Audit / Run History</div>
                <pre className="text-xs whitespace-pre-wrap bg-gray-100 dark:bg-neutral-90 p-2 rounded max-h-48 overflow-auto">
                  {selectedTask.runLog.join('\n')}
                </pre>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* Bulk resubmit confirmation */}
      <Dialog
        header="Confirm Resubmission"
        visible={showConfirm}
        onHide={() => setShowConfirm(false)}
        style={{ width: '42rem' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              pt={{ label: { className: '!text-white' } }}
              onClick={() => setShowConfirm(false)}
            />
            <Button
              label="Resubmit"
              icon="pi pi-replay"
              loading={resubmitting}
              onClick={handleResubmit}
            />
          </div>
        }>
        <div className="space-y-3 text-sm">
          <p>
            You are about to resubmit <strong>{selectedCount}</strong> failed task
            {selectedCount === 1 ? '' : 's'}.
          </p>
          <p>
            These tasks will be submitted using the original submitting user&apos;s credentials and
            Rescale token.
          </p>
          <div className="flex flex-col gap-2">
            <span className="font-medium">Resubmit from</span>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioButton
                  inputId="resubmit-failed"
                  name="resubmitMode"
                  checked={!resubmitFromFirstTask}
                  onChange={() => setResubmitFromFirstTask(false)}
                />
                Failed task
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioButton
                  inputId="resubmit-first"
                  name="resubmitMode"
                  checked={resubmitFromFirstTask}
                  onChange={() => setResubmitFromFirstTask(true)}
                />
                First task in workflow
              </label>
            </div>
            {resubmitFromFirstTask && distinctWorkflowCount < selectedCount && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Some selected tasks share a workflow; they&apos;ll be combined into{' '}
                {distinctWorkflowCount} workflow resubmit{distinctWorkflowCount === 1 ? '' : 's'}.
              </p>
            )}
          </div>
          <div className="border rounded-md border-gray-200 dark:border-neutral-70 max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100 dark:bg-neutral-90">
                <tr className="text-left">
                  <th className="px-2 py-1 font-medium">Task</th>
                  <th className="px-2 py-1 font-medium">Case</th>
                  <th className="px-2 py-1 font-medium">Starts at</th>
                  <th className="px-2 py-1 font-medium">On behalf of</th>
                </tr>
              </thead>
              <tbody>
                {selectedTasks.map(task => {
                  const startsAt =
                    resubmitFromFirstTask && task.firstTaskId ? task.firstTaskId : task.taskId
                  return (
                    <tr
                      key={task.taskId}
                      className="border-t border-gray-100 dark:border-neutral-80">
                      <td className="px-2 py-1 break-words">
                        {task.taskName || task.taskId || '—'}
                      </td>
                      <td className="px-2 py-1 break-words">
                        {task.caseName || task.caseId || '—'}
                      </td>
                      <td className="px-2 py-1 break-words">{startsAt || '—'}</td>
                      <td className="px-2 py-1 break-words">{submitterLabel(task)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p>Do you wish to continue?</p>
        </div>
      </Dialog>
    </div>
  )
}

export default AdminTaskManagement
